/**
 * Tool-result chip (frontend-ux-design.md §4.3 E1 / frontend-display.md §4.2).
 *
 * A compact `bg-accent rounded-md` chip summarizing a completed tool action.
 * When the tool touched an entity it deep-links into the portal — the loop back
 * to the audit surface (§9). Reuses Refine navigation so routing stays
 * consistent with the resource registry.
 */
import { useNavigation } from "@refinedev/core";

import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon";
import type { AssistantToolResultPart } from "@/components/assistant/types";

interface ToolResultChipProps {
  part: AssistantToolResultPart;
}

export function ToolResultChip({ part }: ToolResultChipProps) {
  const { show } = useNavigation();
  const link = part.link;

  const className = cn(
    "inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-accent px-2 py-1 text-xs text-foreground",
    link && "transition-colors hover:bg-accent/70",
  );

  const body = (
    <>
      <span className="shrink-0 text-success">
        <Icon name="resultOk" size={14} aria-hidden />
      </span>
      <span className="truncate">{part.summary}</span>
      {link && (
        <span className="shrink-0 text-muted-foreground">
          <Icon name="deepLink" size={12} aria-hidden />
        </span>
      )}
    </>
  );

  if (!link) {
    return (
      <span data-testid="assistant-tool-result" className={className}>
        {body}
      </span>
    );
  }

  return (
    <button
      type="button"
      data-testid="assistant-tool-result"
      className={className}
      onClick={() => show(link.resource, link.id)}
    >
      {body}
    </button>
  );
}

export default ToolResultChip;
